import { Outlet, useNavigate } from "@tanstack/react-router"
import { Loading } from "modules/shared/components/Loading"
import { Pagination } from "modules/shared/components/Pagination"
import { usePagination } from "modules/shared/hooks/usePagination"
import { useGetCreditTransactions } from "modules/subscription/hooks/useGetCreditTransactions"
import { useSubscriptionData } from "modules/subscription/hooks/useSubscriptionData"
import { useState } from "react"
import { CheckoutDialog } from "../Plans/CheckoutDialog"
import { AddonUsage } from "./AddonUsage"
import { PlanUsage } from "./PlanUsage"
import { UsageTable } from "./UsageTable"

const ITEMS_PER_PAGE = 30

type Props = {
  fullPath: string
  search: {
    page: number
    checkout?: "success" | "cancel"
  }
}

export function Usage({ fullPath, search }: Props) {
  const { page, totalPages, setPage, setCount, count } = usePagination({
    search,
    fullPath,
    total: ITEMS_PER_PAGE,
  })

  const { data: creditTransactions, isPending: isPendingCreditTransactions } =
    useGetCreditTransactions({
      query: { page, total: ITEMS_PER_PAGE },
      onSuccess: (data) => setCount(data.pageInfo.count),
    })

  const {
    subscriptionRenewalDate,
    hasSubscription,
    usedSubscriptionCredits,
    totalSubscriptionCredits,
    subscriptionIsError,
    usedAddonCredits,
    totalAddonCredits,
    addonIsError,
    hasAddon,
    autoTopUpPriceId,
    isPending: isPendingSubscription,
  } = useSubscriptionData()

  const navigate = useNavigate()
  const [checkoutDialogOpen, setCheckoutDialogOpen] = useState(Boolean(search.checkout))

  if (isPendingSubscription || isPendingCreditTransactions) {
    return <Loading />
  }

  return (
    <>
      <div className="flex flex-col gap-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <PlanUsage
            subscriptionIsError={subscriptionIsError}
            subscriptionRenewalDate={subscriptionRenewalDate}
            hasSubscription={hasSubscription}
            usedSubscriptionCredits={usedSubscriptionCredits}
            totalSubscriptionCredits={totalSubscriptionCredits}
          />
          <AddonUsage
            hasAddon={hasAddon}
            addonIsError={addonIsError}
            usedAddonCredits={usedAddonCredits}
            totalAddonCredits={totalAddonCredits}
            autoTopupEnabled={!!autoTopUpPriceId}
          />
        </div>
        <UsageTable creditTransactions={creditTransactions ?? []} />
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          count={count}
        />
      </div>
      {checkoutDialogOpen && (
        <CheckoutDialog
          kind={search.checkout ?? "cancel"}
          onClose={() => {
            setCheckoutDialogOpen(false)
            navigate({ to: "/settings/subscription/usage", search: {} })
          }}
        />
      )}
      <Outlet />
    </>
  )
}
